import ApolloClient, { defaultDataIdFromObject, InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-boost';
import { get } from 'js-cookie';
import Honeybadger from 'honeybadger-js';

import introspectionResult from '../generated/introspectionResult';
import cacheRedirects from '../helpers/cacheRedirects';

const EXPECTED_GRAPHQL_ERROR_MESSAGES = ['Assignment does not exist.'];

const createApolloClient = (uri: string, name: string, fragmentMatcher?: IntrospectionFragmentMatcher) => {
  const cache = new InMemoryCache({
    fragmentMatcher,
    cacheRedirects,
    dataIdFromObject: obj => {
      switch (obj.__typename) {
        // Since RSVP deadline sequence has same id for 2 diff types
        case 'SupportedType':
          return `${obj.__typename}:${obj.id}:${(obj as any).type}`;
        // Guest Messaging Statuses uses batchId
        case 'GuestMessagingStatuses':
          return `${obj.__typename}:${(obj as any).batchId}`;
        // optimizes and reduces unnecessary query calls since we have 2 query calls for Auth and UnAuth users.
        // The ExperimentBucket fragment will be cached and there's no longer a need to make the 2nd query call.
        // experiment_id is used in the cache key ensuring uniqueness for with multiple experiments.
        case 'ExperimentBucket':
          return `${obj.__typename}:${(obj as any).experiment_id}:${(obj as any).bucketing_id}`;
        default:
          return defaultDataIdFromObject(obj);
      }
    },
  });
  return new ApolloClient<typeof cache>({
    onError({ graphQLErrors, networkError }) {
      if (graphQLErrors) {
        for (const error of graphQLErrors) {
          //this check is to prevent the login alert from showing up on the onboarding page
          const isWwsOnboarding = window.location.pathname.includes('/gs/wedding-websites');
          if (
            error.message === 'Response not successful: Received status code 401' ||
            error.message === 'Unauthorized'
          ) {
            if (process.env.NODE_ENV === 'development' && !isWwsOnboarding) {
              alert('Login');
            }
            // Redirect to landing page
            if (!isWwsOnboarding) window.location.assign('/gs/wedding-websites');
            return;
          }

          // Do not send to Honeybadger expected errors
          if (EXPECTED_GRAPHQL_ERROR_MESSAGES.indexOf(error.message) !== -1 || isWwsOnboarding) return;

          Honeybadger.notify(error, {
            name: `GraphQLError: ${error.message}`,
            fingerprint: error.message,
            context: { error, client: name },
          });
        }
      }

      if (networkError) {
        Honeybadger.notify(networkError, {
          name: 'NetworkError',
          context: { client: name },
        });
      }
    },
    uri,
    cache,
    request: operation => {
      operation.setContext({
        headers: {
          token: get('xo-session-token'),
          'X-Api-Key': process.env.REACT_APP_WWS_GRAPHQL_API_KEY,
        },
      });
    },
    name,
    version: process?.env?.REACT_APP_GIT_REVISION || '',
  });
};

// V1 Client (existing)
const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: introspectionResult,
});

const client = createApolloClient(process.env.REACT_APP_WWS_GRAPHQL_ENDPOINT!, 'wws-couple-view', fragmentMatcher);

export const registryShoppingClient = createApolloClient(
  process.env.REACT_APP_REGISTRY_SHOPPING_GRAPHQL_ENDPOINT!,
  'wws-couple-view-registry-shopping'
);

// Public Client for unauthenticated queries (no fragment matcher needed)
const publicEndpoint =
  process.env.REACT_APP_WWS_GRAPHQL_PUBLIC_ENDPOINT || 'https://qa-graphql.guests.theknot.com/public-graphql-v2';

export const publicClient = createApolloClient(publicEndpoint!, 'wws-api-public-client');

// Default export is V1 for backward compatibility
export default client;
